import { z } from 'zod';

/**
 * Lo scadenzario delle fatture (ADR 0254): che cosa resta da pagare ai fornitori e da incassare dai clienti, con la
 * scadenza scritta nel file della fattura. Il residuo è il dovuto meno i collegamenti certi o confermati (ADR 0245); le
 * proposte si vedono ma non contano finché la persona non le conferma.
 */
export type DueState = 'scaduta' | 'in_scadenza' | 'aperta' | 'senza_scadenza' | 'pagata';

/** Una rata della fattura: dal file (DatiPagamento), dal campo della scadenza della voce, o una sola senza data. */
export interface DueInstalmentDto {
  /** AAAA-MM-GG; null = senza scadenza scritta. */
  scadenza: string | null;
  importo: number;
  residuo: number;
  stato: DueState;
  /** Giorni dalla data di oggi alla scadenza: negativi se scaduta; null senza scadenza. */
  giorni: number | null;
}

/** Un collegamento che copre la fattura (o la propone): il movimento si vede solo a chi può leggerlo. */
export interface DueCoverDto {
  id: string;
  importo: number;
  stato: 'certo' | 'proposto' | 'confermato';
  origine: 'regola' | 'persona';
  perche: string | null;
  /** Il movimento è di prima della data della fattura: un acconto. */
  acconto: boolean;
  movimento: { tipo: string; rif: string; nome: string | null; data: string | null } | null;
}

export interface DueItemDto {
  tipo: string;
  rif: string;
  nome: string;
  data: string | null;
  verso: 'da_pagare' | 'da_incassare';
  /** Il nome della controparte, se chi guarda può leggere i soggetti. */
  controparte: string | null;
  ambito: 'azienda' | 'sedi' | 'da_attribuire';
  sedi: string[];
  dovuto: number;
  /** rate: la somma delle rate del file (tiene conto di ritenute e scissione dei pagamenti); importo: il campo della voce. */
  fonte_dovuto: 'rate' | 'importo';
  pagato: number;
  proposto: number;
  residuo: number;
  /** file: le rate della fattura elettronica; voce: il campo della scadenza della voce; null: nessuna scadenza scritta. */
  fonte_scadenza: 'file' | 'voce' | null;
  stato: DueState;
  rate: DueInstalmentDto[];
  coperture: DueCoverDto[];
}

export interface DueTotalsDto {
  scaduto: number;
  in_scadenza: number;
  aperto: number;
  senza_scadenza: number;
}

export interface DuesDto {
  oggi: string;
  /** Quanti giorni avanti conta «in scadenza». */
  giorni: number;
  voci: DueItemDto[];
  /** Le sedi che chi guarda può scegliere, con il nome. */
  sedi: Array<{ id: string; nome: string }>;
  totali: { da_pagare: DueTotalsDto; da_incassare: DueTotalsDto };
  /** Le fatture tolte dall'elenco, con il perché: pagate, senza importo, senza verso, note di credito. */
  esclusi: { pagate: number; senza_importo: number; senza_verso: number; note_di_credito: number };
}

export const DuesQuerySchema = z.object({
  /** «azienda», l'id di una sede, o assente per tutto ciò che si vede. */
  sede: z.union([z.literal('azienda'), z.string().uuid()]).optional(),
  giorni: z.coerce.number().int().min(1).max(365).optional(),
});
export type DuesQuery = z.infer<typeof DuesQuerySchema>;

/** Un movimento che può pagare o incassare una fattura, per il collegamento a mano. */
export interface DueCandidateDto {
  tipo: string;
  rif: string;
  nome: string | null;
  data: string | null;
  descrizione: string | null;
  /** In euro, negativo in uscita. */
  importo: number;
  /** Quanto del movimento non è ancora collegato ad altro. */
  libero: number;
}

export const AddSettlementSchema = z.object({
  movimento: z.object({ tipo: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), rif: z.string().min(1).max(200) }),
  cosa: z.object({ tipo: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), rif: z.string().min(1).max(200) }),
  importo: z.number().positive().max(1e11),
});
export type AddSettlement = z.infer<typeof AddSettlementSchema>;
